import { useState } from 'react';
import { Helmet } from 'react-helmet';
import toast from 'react-hot-toast';
import DataTable from 'react-data-table-component';

import SectionHeader from './SectionHeader';
import TableLoader from './TableLoader';
import CloseSessionModal from './CloseSessionModal';

import useActiveWorkSessions from '../hooks/useActiveWorkSessions';
import workSessionService from '../services/workSessionService';
import { formatDateToSydneyTimeZone } from '../utils/dateUtils';

const formatDuration = (startTime) => {
  const diffMs = Date.now() - new Date(startTime).getTime();
  const totalMinutes = Math.floor(diffMs / 60000);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

const AdminLiveSessions = () => {
  const { sessions, isLoading, mutate } = useActiveWorkSessions();

  const [selectedSession, setSelectedSession] = useState(null);
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const [isClosingSession, setIsClosingSession] = useState(false);

  const handleCloseClicked = (session) => {
    setSelectedSession(session);
    setIsCloseModalOpen(true);
  };

  const handleCancel = () => {
    setIsCloseModalOpen(false);
    setSelectedSession(null);
  };

  const handleConfirmClose = async (endTime) => {
    try {
      setIsClosingSession(true);
      await workSessionService.adminCloseSession(selectedSession._id, endTime);
      await mutate();
      toast.success('Session closed');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsClosingSession(false);
      setIsCloseModalOpen(false);
      setSelectedSession(null);
    }
  };

  const columns = [
    {
      name: 'Labourer',
      selector: row => row.labourer?.user?.fullName,
      sortable: true
    },
    {
      name: 'Project',
      selector: row => row.project?.name,
      sortable: true
    },
    {
      name: 'Clocked In',
      selector: row => row.startTime,
      format: row => formatDateToSydneyTimeZone(row.startTime, 'dd/MM/yyyy HH:mm'),
      sortable: true
    },
    {
      name: 'Duration',
      cell: row => formatDuration(row.startTime)
    },
    {
      right: 'true',
      cell: row => (
        <button
          onClick={() => handleCloseClicked(row)}
          className="py-1 px-2 text-xs font-semibold cursor-pointer rounded-md bg-red-600 text-white text-nowrap"
        >
          Close
        </button>
      )
    }
  ];

  return (
    <>
      <Helmet>
        <title>Clocked In | ASE Group</title>
      </Helmet>

      <div className="border border-ase-blue rounded-lg pb-1">
        <SectionHeader
          title="Clocked In"
          actionsLeft={
            <p className="text-xs text-white opacity-75">Refreshes every 60s</p>
          }
        />

        <DataTable
          columns={columns}
          data={sessions}
          striped
          highlightOnHover
          progressPending={isLoading}
          progressComponent={<TableLoader />}
          noDataComponent={<p className="text-gray-500 text-sm py-8">No labourers are currently clocked in.</p>}
        />
      </div>

      <CloseSessionModal
        isOpen={isCloseModalOpen}
        session={selectedSession}
        isClosing={isClosingSession}
        onConfirm={handleConfirmClose}
        onCancel={handleCancel}
      />
    </>
  );
};

export default AdminLiveSessions;
